import { CheckIcon, FilterIcon, Popover, cn } from '@quickmd-llc/design-system';

export type FilterOption = { value: string; label: string };

type Props = {
  title: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
};

// A multi-select column filter rendered in a Popover. Selecting nothing means
// "no filter" (all rows); selecting one or more narrows to those values.
export function ColumnFilterMenu({ title, options, selected, onChange }: Props) {
  const isActive = selected.length > 0;
  const allSelected = selected.length === options.length;

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  const trigger = (
    <button
      type="button"
      aria-label={`Filter ${title}`}
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded-md text-text-light hover:bg-shade-05 hover:text-text-dark',
        isActive && 'bg-coastal-blue-10 text-text-highlight',
      )}
    >
      <FilterIcon iconSize="xs" />
    </button>
  );

  return (
    <Popover trigger={trigger} placement="bottom-end">
      <div className="tailwind-scoped">
        <div className="min-w-[190px] rounded-large border border-border-medium bg-white p-sp-1 shadow-lg">
          <div className="px-sp-1 pb-sp-0.5 pt-sp-0.5 text-xs font-semibold uppercase tracking-wide text-text-light">
            {title}
          </div>

          <button
            type="button"
            className="flex w-full items-center gap-sp-1 rounded-md px-sp-1 py-sp-0.75 text-left text-sm font-semibold text-text-highlight hover:bg-coastal-blue-05"
            onClick={() => onChange(allSelected ? [] : options.map((o) => o.value))}
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </button>

          <div className="my-sp-0.5 h-px bg-shade-10" />

          {options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                className="flex w-full items-center gap-sp-1 rounded-md px-sp-1 py-sp-0.75 text-left text-sm text-text-dark hover:bg-coastal-blue-05"
                onClick={() => toggle(option.value)}
              >
                <span className="flex h-4 w-4 items-center justify-center text-text-highlight">
                  {checked ? <CheckIcon iconSize="xs" /> : null}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </Popover>
  );
}
